import type { ColumnDef } from "@tanstack/react-table";
import { buildActionColumn, buildAvatarDetailsColumn, buildCopyTextColumn, buildTagColumn } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import type { GrpEnrollmentRow } from "../types";
import { ENROLLMENT_STATUS_LABELS, GrpEnrollmentStatus, PENDING_DOLLARS_PLACEHOLDER } from "../types";

interface ColumnsOptions {
  isCompactLayout: boolean;
  setMenu: (opts: { rowId: string; actions: Action[] }) => void;
  onViewPerson: (pgaId: string) => void;
}

export function enrollmentPersonId(row: GrpEnrollmentRow) {
  return row.person?.pgaId || row.memberNumber;
}

export function getColumns({
  isCompactLayout,
  setMenu,
  onViewPerson
}: ColumnsOptions): ColumnDef<GrpEnrollmentRow, unknown>[] {
  return [
    ...buildAvatarDetailsColumn<GrpEnrollmentRow>({
      isCompactLayout,
      getTitle: (row) => row.person?.displayName || row.memberNumber,
      getSubtitle: (row) => row.memberNumber,
      getPicture: (row) => row.person?.profilePhoto ?? undefined,
      header: "Name",
      size: 230,
      subtitleLabel: "PGA ID",
      id: "displayName",
      sortType: "text"
    }),
    buildCopyTextColumn<GrpEnrollmentRow>({
      accessorKey: "memberNumber",
      header: "PGA ID",
      getText: (row) => row.memberNumber,
      copyFieldLabel: "PGA ID",
      shouldShowCopyButton: (row) => !!row.memberNumber,
      enableSorting: false,
      size: 120
    }),
    {
      accessorKey: "classification",
      header: "Class",
      size: 100,
      enableSorting: false,
      cell: ({ getValue }) => renderEmptyCellState(getValue())
    },
    {
      accessorKey: "state",
      header: "State",
      size: 80,
      enableSorting: false,
      cell: ({ getValue }) => renderEmptyCellState(getValue())
    },
    {
      accessorKey: "facilityName",
      header: "Facility",
      size: 240,
      enableSorting: false,
      cell: ({ getValue }) => renderEmptyCellState(getValue())
    },
    {
      accessorKey: "accountOpenedOn",
      header: "Date enrolled",
      size: 140,
      meta: { sortType: "date" },
      cell: ({ row }) => row.original.enrolledOnLabel
    },
    {
      accessorKey: "pendingDollars",
      header: "Pending dollars",
      size: 150,
      enableSorting: false,
      meta: { className: "text-right", headerClassName: "justify-end text-right" },
      cell: () => PENDING_DOLLARS_PLACEHOLDER
    },
    buildTagColumn<GrpEnrollmentRow>({
      accessorKey: "status",
      header: "Status",
      getTagLabel: (row) => ENROLLMENT_STATUS_LABELS[row.status],
      getTagType: (row) => (row.status === GrpEnrollmentStatus.ACTIVE ? "success" : "surface"),
      getTagStyle: () => "strong",
      size: 120
    }),
    buildActionColumn<GrpEnrollmentRow>({
      onViewClick: (row) => {
        const pgaId = enrollmentPersonId(row);
        if (pgaId) onViewPerson(pgaId);
      },
      showViewButton: (row) => !!enrollmentPersonId(row),
      viewButtonTitle: "View person details",
      actionId,
      setMenu
    })
  ];
}
